import { LatLng } from 'leaflet';
import { point as turfPoint } from '@turf/helpers';
import { distance as turfDistance } from '@turf/distance';
import { parseZonedDateTime } from '@internationalized/date';
import { MINUTE_IN_MS } from '@/constants/date.constants';
import { queryClient } from '@/queries';
import { hereMapsQueries } from '@/queries/here-maps';
import { AIR_BLOCK_TIME } from '@/utils/route/config';
import { CalculateGroundTimeType } from '@/api/apis/here-maps';

export function calculateFlightDurationMs(start: LatLng, end: LatLng): number {
  const startPoint = turfPoint([start.lng, start.lat]);
  const endPoint = turfPoint([end.lng, end.lat]);
  const distanceKm = turfDistance(startPoint, endPoint);

  // I don't know what all these floats mean, I've just copied it from mission planner algorithm.
  const hours =
    0.38801520941928963 +
    0.0014824083459939498 * distanceKm +
    0.052401670393339726 * start.lng +
    0.002593045382823183 * start.lat +
    -0.05009961680316368 * end.lng +
    -0.007839625921447331 * end.lat +
    -2.6567831486228757e-8 * distanceKm ** 2 +
    -1.7689652573544625e-6 * (distanceKm * start.lng) +
    -3.45264514948248e-7 * (distanceKm * start.lat) +
    1.3652656336330684e-6 * (distanceKm * end.lng) +
    -1.0208229754096504e-6 * (distanceKm * end.lat) +
    -1.179284560740872e-5 * start.lng ** 2 +
    -0.0003453562582683804 * (start.lng * start.lat) +
    0.0001285461915800128 * (start.lng * end.lng) +
    -0.00032497366028093273 * (start.lng * end.lat) +
    -0.00040933107419976886 * start.lat ** 2 +
    0.00033988544707636725 * (start.lat * end.lng) +
    0.0007120750601324052 * (start.lat * end.lat) +
    -0.00010012104758178276 * end.lng ** 2 +
    0.00036441815842881416 * (end.lng * end.lat) +
    -0.00019760988595528828 * end.lat ** 2;

  return Math.round(hours * 60 * 60 * 1000);
}

export const getFlightDuration = (departure: LatLng, arrival: LatLng): number => {
  const timeDuration = calculateFlightDurationMs(departure, arrival);
  const secondsOffset = timeDuration % MINUTE_IN_MS;

  return timeDuration - secondsOffset + MINUTE_IN_MS;
};

export const calculateAirTimeDuration = (departureData: LatLng, arrivalData: LatLng) => {
  const timeDuration = getFlightDuration(departureData, arrivalData);
  const durationInMs = timeDuration + AIR_BLOCK_TIME;

  return { timeDurationInMs: durationInMs, distanceInMeters: 0 };
};

export const calculateGroundTimeDuration = async (
  departureData: LatLng,
  arrivalData: LatLng,
  type: CalculateGroundTimeType,
  dateTimeString: string,
) => {
  const dateTimeUtc = parseZonedDateTime(dateTimeString).toAbsoluteString();

  const data = await queryClient.ensureQueryData(
    hereMapsQueries.getGroundRoute({
      startLocation: departureData,
      endLocation: arrivalData,
      type,
      timestamp: dateTimeUtc,
    }),
  );

  if (data.summary) {
    const secondsOffset = (data.summary.duration * 1000) % MINUTE_IN_MS;
    const roundedMinutesMs = data.summary.duration * 1000 - secondsOffset + MINUTE_IN_MS;

    return {
      timeDurationInMs: data.isFeasible ? roundedMinutesMs : -1,
      distanceInMeters: data.summary.length,
    };
  }

  return { timeDurationInMs: -1, distanceInMeters: 0 };
};
